import React, { useCallback, useEffect, useMemo, useState } from 'react';

const normalizeIdentitySegment = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

const BottomControls = ({
  seasonPlayers = [],
  communityAssetPacks = [],
  seasonLogo = null,
  assetsLoading = false,
  teamAssetsSide,
  playersOnCanvas,
  activeStep,
  utilitiesOnCanvas,
  textBoxes,
  drawnLines,
  imagesOnCanvas,
  visionCones,
  hoverMenuTimeout,
  historyRef,
  dragJustEndedRef,
  setTeamAssetsSide,
  setPlayersOnCanvas,
  setActiveTool,
  setIsPenPanelVisible,
  setHoveredPlayerMenu,
  generateUniqueId,
}) => {
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [communityPackSides, setCommunityPackSides] = useState({});

  const hasSeasonAssets = (seasonPlayers && seasonPlayers.length > 0) || !!seasonLogo;

  const getPackPlayers = useCallback((pack = {}) => {
    let players = [];
    const packId = String(pack?.id || pack?.sourceLibraryId || pack?.sourceTeam || 'community-pack');
    const sourceTeamName = pack?.sourceTeamName || pack?.teamName || pack?.name || 'Imported Team';

    if (Array.isArray(pack.players) && pack.players.length > 0) {
      players = pack.players;
    } else if (Array.isArray(pack.roster) && pack.roster.length > 0) {
      players = pack.roster;
    } else if (Array.isArray(pack.playerPics) && pack.playerPics.length > 0) {
      players = pack.playerPics.map((pic, idx) => ({
        name: `Player ${idx + 1}`,
        avatar: pic,
      }));
    }

    return players
      .map((player, idx) => {
        const playerId = player?.id || player?.sourcePlayerId || null;
        const name = player?.name || player?.ign || `Player ${idx + 1}`;
        const identitySegment = playerId || normalizeIdentitySegment(name) || idx;

        return {
          id: playerId,
          name,
          avatar: player?.avatar || player?.image || player?.pic || player?.url || '',
          side: player?.side,
          source: 'community',
          sourcePackId: packId,
          sourceTeam: pack?.sourceTeam || pack?.teamId || null,
          sourceTeamName,
          sourcePlayerId: playerId,
          sourcePlayerIdentity: player?.sourcePlayerIdentity || `${packId}:${identitySegment}`,
        };
      })
      .filter((player) => !!player.avatar);
  }, []);

  const normalizedCommunityPacks = useMemo(
    () => (Array.isArray(communityAssetPacks) ? communityAssetPacks : []).map((pack, idx) => ({
      id: String(pack?.id || pack?.sourceLibraryId || pack?.sourceTeam || `community-pack-${idx}`),
      title: pack?.sourceTeamName || pack?.sourceTeam || pack?.teamName || pack?.name || 'Imported Team',
      logo: pack?.logo || pack?.teamLogo || pack?.image || pack?.url || null,
      players: getPackPlayers(pack),
    })),
    [communityAssetPacks, getPackPlayers]
  );

  const hasCommunityAssets = assetsLoading || normalizedCommunityPacks.length > 0;

  useEffect(() => {
    const selectedStillExists =
      selectedRoster?.type === 'season'
        ? hasSeasonAssets
        : normalizedCommunityPacks.some((pack) => pack.id === selectedRoster?.id);

    if (selectedStillExists) return;

    if (hasSeasonAssets) {
      setSelectedRoster({ type: 'season', id: 'season' });
      return;
    }

    if (normalizedCommunityPacks.length > 0) {
      setSelectedRoster({ type: 'community', id: normalizedCommunityPacks[0].id });
      return;
    }

    setSelectedRoster(null);
  }, [hasSeasonAssets, normalizedCommunityPacks, selectedRoster]);

  const getCommunityPackSide = (packId) => communityPackSides[packId] || 'ally';

  const setCommunityPackSide = (packId, side) => {
    setCommunityPackSides((prev) => ({
      ...prev,
      [packId]: side,
    }));
  };

  const selectedCommunityPack =
    selectedRoster?.type === 'community'
      ? normalizedCommunityPacks.find((pack) => pack.id === selectedRoster.id)
      : null;

  const selectedRosterData =
    selectedRoster?.type === 'season'
      ? {
          type: 'season',
          id: 'season',
          title: 'Your Team',
          meta: `${seasonPlayers.length} players`,
          players: seasonPlayers,
          side: teamAssetsSide || 'ally',
        }
      : selectedCommunityPack
        ? {
            type: 'community',
            id: selectedCommunityPack.id,
            title: selectedCommunityPack.title,
            meta: `${selectedCommunityPack.players.length} players`,
            players: selectedCommunityPack.players,
            side: getCommunityPackSide(selectedCommunityPack.id),
          }
        : null;

  const setSelectedRosterSide = (side) => {
    if (!selectedRosterData) return;

    if (selectedRosterData.type === 'community') {
      setCommunityPackSide(selectedRosterData.id, side);
      return;
    }

    setTeamAssetsSide(side);
  };

  const handlePlayerClick = (player, isCustom = false, pidx = null, placementSide = teamAssetsSide || 'ally') => {
    const playerCount = playersOnCanvas.length;
    const column = playerCount % 5;
    const row = Math.floor(playerCount / 5);
    const relX = 5 + column * 5;
    const relY = 5 + row * 7;

    const newPlayer = {
      id: generateUniqueId(),
      name: isCustom ? (player.name || `Player ${pidx + 1}`) : player.name,
      avatar: isCustom ? (player.avatar || player.image || player.pic || player.url) : player.avatar,
      relX,
      relY,
      side: placementSide,
      sourcePackId: player.sourcePackId,
      sourceTeam: player.sourceTeam,
      sourceTeamName: player.sourceTeamName,
      sourcePlayerId: player.sourcePlayerId,
      sourcePlayerIdentity: player.sourcePlayerIdentity,
    };

    setPlayersOnCanvas((prev) => {
      const updated = [...prev, newPlayer];

      const h = historyRef.current[activeStep];
      const snapshot = {
        playersOnCanvas: updated,
        utilitiesOnCanvas,
        textBoxes,
        drawnLines,
        imagesOnCanvas,
        visionCones,
      };
      const last = h.past[h.past.length - 1];
      const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
      if (!isDuplicate) {
        h.past.push(snapshot);
        h.future = [];
      }

      dragJustEndedRef.current = true;
      return updated;
    });

    setActiveTool(null);
    setIsPenPanelVisible(false);
  };

  const handlePlayerDragStart = (e, player, isCustom = false, pidx = null, placementSide = teamAssetsSide || 'ally') => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'player',
        id: player.id || pidx,
        name: isCustom ? (player.name || `Player ${pidx + 1}`) : player.name,
        avatar: isCustom ? (player.avatar || player.image || player.pic || player.url) : player.avatar,
        side: placementSide,
        sourcePackId: player.sourcePackId,
        sourceTeam: player.sourceTeam,
        sourceTeamName: player.sourceTeamName,
        sourcePlayerId: player.sourcePlayerId,
        sourcePlayerIdentity: player.sourcePlayerIdentity,
      })
    );
  };

  const renderPlayerCard = (player, pidx, color, onClick, onDragStart) => (
    <div
      key={pidx}
      className="player-card asset-player-card"
      style={{ background: color }}
      onMouseEnter={(e) => {
        if (hoverMenuTimeout.current) clearTimeout(hoverMenuTimeout.current);
        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredPlayerMenu({ idx: pidx, rect });
      }}
      onMouseLeave={() => {
        if (hoverMenuTimeout.current) clearTimeout(hoverMenuTimeout.current);
        hoverMenuTimeout.current = setTimeout(() => setHoveredPlayerMenu(null), 120);
      }}
    >
      <img
        src={player.avatar || player.image || player.pic || player.url}
        alt={player.name || `Player ${pidx + 1}`}
        draggable
        onClick={onClick}
        onDragStart={onDragStart}
        className="asset-player-avatar"
      />
      <span className="asset-player-name">{player.name || `Player ${pidx + 1}`}</span>
    </div>
  );

  return (
    <div className="bottom-panel" onContextMenu={(e) => e.preventDefault()}>
      {(hasSeasonAssets || hasCommunityAssets) && (
        <div className="strategy-asset-dock selected-roster-model">
          <div className="asset-dock-main-heading">Roster & Assets</div>

          <div className="roster-selector-row">
            <section className="roster-selector-section roster-selector-season" aria-label="Your team">
              <div className="asset-dock-heading">Your Team</div>
              {hasSeasonAssets ? (
                <button
                  type="button"
                  className={`asset-card roster-select-card ${selectedRoster?.type === 'season' ? 'is-selected' : ''}`}
                  onClick={() => setSelectedRoster({ type: 'season', id: 'season' })}
                  aria-pressed={selectedRoster?.type === 'season'}
                >
                  <span className="asset-logo-wrap">
                    {seasonLogo ? (
                      <img src={seasonLogo} alt="Season roster" className="asset-logo" />
                    ) : (
                      <span className="asset-logo-fallback">SR</span>
                    )}
                  </span>
                  <span className="asset-card-copy">
                    <span className="asset-card-title">Your Team</span>
                    <span className="asset-card-meta">{seasonPlayers.length} players</span>
                  </span>
                </button>
              ) : (
                <div className="asset-empty">No season roster</div>
              )}
            </section>

            <section className="roster-selector-section roster-selector-community" aria-label="Community teams">
              <div className="asset-dock-heading">Community Teams</div>
              {assetsLoading ? (
                <div className="asset-empty">Loading shared teams...</div>
              ) : (
                <div className="community-pack-strip">
                  {normalizedCommunityPacks.map((pack) => (
                    <button
                      key={pack.id}
                      type="button"
                      className={`asset-card roster-select-card community-roster-card ${
                        selectedRoster?.type === 'community' && selectedRoster.id === pack.id ? 'is-selected' : ''
                      }`}
                      onClick={() => setSelectedRoster({ type: 'community', id: pack.id })}
                      aria-pressed={selectedRoster?.type === 'community' && selectedRoster.id === pack.id}
                    >
                      <span className="asset-logo-wrap">
                        {pack.logo ? (
                          <img src={pack.logo} alt={pack.title} className="asset-logo" />
                        ) : (
                          <span className="asset-logo-fallback">{pack.title.slice(0, 2).toUpperCase()}</span>
                        )}
                      </span>
                      <span className="asset-card-copy">
                        <span className="asset-card-title">{pack.title}</span>
                        <span className="asset-card-meta">{pack.players.length} players</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>

          {selectedRosterData && (
            <section className="selected-roster-tray" aria-label={`${selectedRosterData.title} players`}>
              <div className="selected-roster-head">
                <div className="selected-roster-title-wrap">
                  <span className="selected-roster-title">{selectedRosterData.title}</span>
                  <span className="selected-roster-meta">{selectedRosterData.meta}</span>
                </div>

                <div className="selected-roster-side">
                  <span className="asset-side-label">Side</span>
                  <div className="team-side-toggle compact">
                    <button
                      type="button"
                      className={`toggle-option ${selectedRosterData.side === 'ally' ? 'active' : ''}`}
                      onClick={() => setSelectedRosterSide('ally')}
                    >
                      Ally
                    </button>
                    <button
                      type="button"
                      className={`toggle-option ${selectedRosterData.side === 'enemy' ? 'active' : ''}`}
                      onClick={() => setSelectedRosterSide('enemy')}
                    >
                      Enemy
                    </button>
                  </div>
                </div>
              </div>

              <div className="asset-player-tray shared-roster-player-tray">
                {selectedRosterData.players.length === 0 ? (
                  <div className="asset-empty">No players configured yet.</div>
                ) : (
                  selectedRosterData.players.map((player, pidx) =>
                    renderPlayerCard(
                      player,
                      `${selectedRosterData.type}-${selectedRosterData.id}-${pidx}`,
                      selectedRosterData.side === 'enemy' ? '#d97706' : '#1e3a8a',
                      () => handlePlayerClick(player, true, pidx, selectedRosterData.side),
                      (e) => handlePlayerDragStart(e, player, true, pidx, selectedRosterData.side)
                    )
                  )
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default BottomControls;
